import React from 'react';
import { useTranslation } from 'react-i18next';

import { Container, Icon, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';
import { DynamicMediaItem } from '@uncinc/react-drupal-core';
import { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { dateFormatter } from '../../../../helpers';
import { translateEmploymentTypes } from '../../../../helpers/translatedEmploymentTypes';
import { NodeVacancy } from '../../../../types/uncinc_profile/nodes';
import { ParagraphsRenderer } from '../../../../components';
import { DEFAULT_LANGUAGE } from '../../../../config/constants';

import './style.scss';

interface NodeVacancyProps extends React.ComponentPropsWithoutRef<'div'> {
  entity: ImmutableEntity<NodeVacancy>;
}

const NodeVacancyPage: React.FC<NodeVacancyProps> = ({ entity }) => {
  const { t } = useTranslation();
  const vacancy = entity.toJS();
  const title = vacancy.title[0].value;
  const image = vacancy?.field_media_item[0];
  const paragraphs = vacancy.field_paragraphs;
  const body = vacancy.body[0]?.processed;
  const date = vacancy.field_publish_date[0]?.value || vacancy.created[0]?.value;
  const themes = vacancy.field_themes;
  const lang = vacancy.langcode[0]?.value || DEFAULT_LANGUAGE;

  const location = vacancy.field_location[0];
  const streetAddress = [
    location?.entity.field_address[0]?.address_line1,
    location?.entity.field_address[0]?.address_line2,
    location?.entity.field_address[0]?.address_line3,
  ].join(' ');

  const hoursMinimum = vacancy.field_hours_minimum[0]?.value;
  const hoursMaximum = vacancy.field_hours_maximum[0].value;
  const salaryMinimum = vacancy.field_salary_minimum[0];
  const salaryMaximum = vacancy.field_salary_maximum[0];
  const validThrough = vacancy.field_valid_through;
  const salaryType = vacancy.field_salary_type[0];

  const employmentTypes = vacancy.field_employment_types;
  const translatedEmploymentTypes = translateEmploymentTypes(
    vacancy.field_employment_types,
    lang,
  );

  const benefits = vacancy.field_benefits;
  const requirements = vacancy.field_requirements;

  const schema = JSON.stringify({
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    title,
    description: body,
    identifier: {
      '@type': 'PropertyValue',
      name: 'Unc Inc BV',
      value: '64721868',
    },
    datePosted: date,
    validThrough: validThrough[0]?.value,
    employmentType: employmentTypes.map((type) => type.value),
    hiringOrganization: {
      '@type': 'Organization',
      name: 'Unc Inc BV',
      sameAs: 'https://www.uncinc.nl',
      logo: 'https://www.example.com/images/logo.png', // CHANGE ME
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        streetAddress,
        postalCode: location?.entity.field_address[0]?.postal_code,
        addressCountry: location?.entity.field_address[0]?.country_code,
      },
    },
    baseSalary: {
      '@type': 'MonetaryAmount',
      currency: salaryMaximum.currency_code,
      value: {
        '@type': 'QuantitativeValue',
        minValue: salaryMinimum?.number,
        maxValue: salaryMaximum.number,
        unitText: salaryType,
      },
    },

  });

  return (
    <>
      <script type="application/ld+json">
        {schema}
      </script>

      <article itemType="http://schema.org/WebPage" className="NodeVacancyPage">
        <Container small>
          <div className="NodeVacancyPage__hero">
            <div className="NodeVacancyPage__hero__meta">
              <p className="NodeVacancyPage__hero__meta__type">
                {t('content_types.vacancy.type')}
              </p>
              <span className="dot">&bull;</span>
              <p>{dateFormatter(date, 'nl')}
              </p>
            </div>
            <div className="NodeVacancyPage__hero__text">
              <h1 itemProp="NodeVacancyPage__hero__title">{title}</h1>
              {body && (
                <WysiwygField
                  className="NodeVacancyPage__hero__body"
                  property="schema:text"
                  html={body}
                />
              )}
            </div>
          </div>

          <div>{translatedEmploymentTypes}</div>

          {hoursMinimum && (
            <div>{hoursMinimum}</div>
          )}
          <div>{hoursMaximum}</div>
          {image && (
            <DynamicMediaItem
              media={image}
            />
          )}

          {themes.map((theme) => (
            <div>{theme.entity.name[0].value}</div>
          ))}

          {benefits.map((benefit) => (
            <div>
              {benefit.entity.field_icon[0] && (
                <Icon
                  name={benefit.entity.field_icon[0].icon}
                  flair={benefit.entity.field_icon[0].family}
                />
              )}
              {benefit.entity.name[0].value}
            </div>
          ))}

          {requirements.map((requirement) => (
            <div>
              {requirement.entity.field_icon[0] && (
                <Icon
                  name={requirement.entity.field_icon[0].icon}
                  flair={requirement.entity.field_icon[0].family}
                />
              )}
              {requirement.entity.name[0].value}
            </div>
          ))}

        </Container>

        <ParagraphsRenderer className="NodeVacancyPage__paragraphs" paragraphs={paragraphs} />
      </article>
    </>

  );
};

export default NodeVacancyPage;
